// ChannelReadService.js

// Firebase
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

const WORKSPACES_COLLECTION = collections.workspaces;

const channelsDatabaseRef = (workspaceKey) => (
  collection(db, WORKSPACES_COLLECTION, workspaceKey, 'channels')
);

const channelDocRef = (workspaceKey, channelKey) => (
  doc(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey)
);

const channelMessagesDatabaseRef = (workspaceKey, channelKey) => (
  collection(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey, 'messages')
);

const channelReadDocRef = (workspaceKey, channelKey, userKey) => (
  doc(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey, 'reads', userKey)
);

const getChannelSortValue = (channel) => (
  String(channel?.titleLower || channel?.title || '').trim().toLowerCase()
);

/**
 * Subscribes to workspace channels in real time.
 *
 * @param {string} workspaceKey - Workspace key.
 * @param {function} callback - Callback receiving channel rows.
 * @param {function} onError - Optional callback receiving errors.
 * @param {object} options - Optional query options.
 * @param {string} options.viewerUserKey - Optional user key to scope channels by membership.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToWorkspaceChannels(workspaceKey, callback, onError, options = {}) {
  if (!workspaceKey || typeof callback !== 'function') {
    return () => {};
  }

  const q = query(
    channelsDatabaseRef(workspaceKey),
    orderBy('titleLower', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
        key: docSnap.id,
      }));

      rows.sort((a, b) => getChannelSortValue(a).localeCompare(getChannelSortValue(b)));
      callback(rows);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToWorkspaceChannels error:', error);
      callback([]);
    }
  );
}

/**
 * Subscribes to a single channel.
 *
 * @param {string} workspaceKey - Workspace key.
 * @param {string} channelKey - Channel key.
 * @param {function} callback - Callback receiving channel row.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannel(workspaceKey, channelKey, callback, onError) {
  if (!workspaceKey || !channelKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    channelDocRef(workspaceKey, channelKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id }
        : null;
      callback(row);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannel error:', error);
      callback(null);
    }
  );
}

/**
 * Subscribes to channel messages in real time.
 *
 * @param {string} workspaceKey - Workspace key.
 * @param {string} channelKey - Channel key.
 * @param {function} callback - Callback receiving message rows.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannelMessages(workspaceKey, channelKey, callback, onError) {
  if (!workspaceKey || !channelKey || typeof callback !== 'function') {
    return () => {};
  }

  const q = query(
    channelMessagesDatabaseRef(workspaceKey, channelKey),
    orderBy('dateCreated', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
        key: docSnap.id,
      }));
      callback(rows);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannelMessages error:', error);
      callback([]);
    }
  );
}

/**
 * Subscribes to a user's read receipt for one channel.
 *
 * @param {string} workspaceKey - Workspace key.
 * @param {string} channelKey - Channel key.
 * @param {string} userKey - User key.
 * @param {function} callback - Callback receiving receipt or null.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannelReadReceipt(workspaceKey, channelKey, userKey, callback, onError) {
  if (!workspaceKey || !channelKey || !userKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    channelReadDocRef(workspaceKey, channelKey, userKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}) }
        : null;
      callback(row);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannelReadReceipt error:', error);
      callback(null);
    }
  );
}
